import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, IconButton, Paper, Tooltip, CircularProgress } from "@mui/material";
import { Bolt as FlashIcon, Stop as StopIcon } from "@mui/icons-material";
import io from "socket.io-client";
import { useSnackbar } from "notistack";

const FlashQuizStatusBox = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [activeFlashSession, setActiveFlashSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [stoppingQuestion, setStoppingQuestion] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [currentSong, setCurrentSong] = useState(null); // { name, artist }
  const [hiddenUntilNextPush, setHiddenUntilNextPush] = useState(false);
  const socketRef = useRef(null);

  // Fetch active flash quiz session
  const fetchActiveFlashSession = useCallback(async () => {
    try {
      setLoadingSession(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/live-quiz/flash/active`
      );

      if (!response.ok) throw new Error("Failed to fetch");

      const data = await response.json();
      if (data.sessions && data.sessions.length > 0) {
        const session = data.sessions[0];
        setActiveFlashSession(session);

        // Try to extract song info from rapid_config if available
        try {
          const rapid =
            typeof session.rapid_config === "string"
              ? JSON.parse(session.rapid_config)
              : session.rapid_config || {};

          const songFromConfig =
            rapid.current_song ||
            rapid.currentSong ||
            rapid.song ||
            null;

          if (songFromConfig) {
            setCurrentSong({
              name: songFromConfig.name || songFromConfig.title || "Unknown song",
              artist: songFromConfig.artist || songFromConfig.singer || "",
            });
          } else if (rapid.current_song_name || rapid.current_song_artist) {
            setCurrentSong({
              name: rapid.current_song_name || "Unknown song",
              artist: rapid.current_song_artist || "",
            });
          }
        } catch {
          // Ignore parsing errors, song name is optional
        }
      } else {
        setActiveFlashSession(null);
        setCurrentSong(null);
      }
    } catch (error) {
      console.error("[FlashQuizStatusBox] Error fetching flash session:", error);
    } finally {
      setLoadingSession(false);
    }
  }, []);

  // WebSocket connection for real-time updates
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
    const socket = io(baseUrl, { transports: ["websocket"] });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("flash:join-global");
    });

    socket.on("flash:question_pushed", async (payload) => {
      if (payload && payload.session_id) {

        // A new question is running, show the widget again
        setHiddenUntilNextPush(false);

        // Update song info directly from payload.current_song if available
        if (payload.current_song) {
          setCurrentSong({
            name: payload.current_song.name || payload.current_song.title || "Unknown song",
            artist: payload.current_song.artist || payload.current_song.singer || "",
          });
        }

        await fetchActiveFlashSession();
      }
    });

    socket.on("flash:question_stopped", async (payload) => {
      if (payload && payload.session_id) {
        await fetchActiveFlashSession();
        setCurrentSong(null);
        // Keep it hidden until a fresh push comes in
        setHiddenUntilNextPush(true);
      }
    });

    socket.on("flash:session_ended", async (payload) => {
      if (payload && payload.session_id) {
        setActiveFlashSession((prev) => {
          if (prev && prev.session_id === payload.session_id) {
            return null;
          }
          return prev;
        });
        setCurrentSong(null);
        setHiddenUntilNextPush(false);
      }
    });

    socket.on("flash:activated", async (payload) => {
      if (payload && payload.session_id) { 
        await fetchActiveFlashSession();
      }
    });

    return () => {
      if (socketRef.current) { 
        socketRef.current.emit("flash:leave-global");
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [fetchActiveFlashSession]);

  // Initial session fetch
  useEffect(() => {
    fetchActiveFlashSession();
  }, [fetchActiveFlashSession]);

  // Tick current time every second for timer display
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getTimerState = useCallback(() => {
    if (!activeFlashSession?.rapid_config) return null;

    try {
      const rapidConfig =
        typeof activeFlashSession.rapid_config === "string"
          ? JSON.parse(activeFlashSession.rapid_config)
          : activeFlashSession.rapid_config;

      const pushedAt = rapidConfig.question_pushed_at;
      const deliveryTime = rapidConfig.question_delivery_time;
      const songTimerSeconds = rapidConfig.song_timer_seconds || 60;

      if (!pushedAt || !deliveryTime) return null;

      const now = currentTime;
      const delivery = new Date(deliveryTime).getTime();
      const quizletEnd = delivery + songTimerSeconds * 1000;

      if (now < delivery) {
        const remaining = Math.max(0, Math.ceil((delivery - now) / 1000));
        return {
          phase: "delay",
          remainingSeconds: remaining,
          songTimerSeconds,
        };
      }

      if (now < quizletEnd) {
        const remaining = Math.max(0, Math.ceil((quizletEnd - now) / 1000));
        return {
          phase: "quizlet",
          remainingSeconds: remaining,
          songTimerSeconds,
        };
      }

      return null;
    } catch (e) {
      console.warn("[FlashQuizStatusBox] Failed to parse rapid_config:", e?.message);
      return null;
    }
  }, [activeFlashSession, currentTime]);

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleStopQuestion = async () => {
    if (!activeFlashSession) {
      enqueueSnackbar("No active flash quiz session", { variant: "warning" });
      return;
    }

    try {
      setStoppingQuestion(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/live-quiz/flash/sessions/${
          activeFlashSession.session_id
        }/stop-question`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to stop question");
      }

      enqueueSnackbar("Question stopped successfully", { variant: "success" });
      // Immediately hide the widget after a successful stop on this page.
      // It will re-appear only when a new question is pushed.
      setHiddenUntilNextPush(true);
    } catch (error) {
      console.error("[FlashQuizStatusBox] Error stopping question:", error);
      enqueueSnackbar(error.message || "Failed to stop question", {
        variant: "error",
      });
    } finally {
      setStoppingQuestion(false);
    }
  };

  const timerState = getTimerState();

  // Hide widget if there's no active session, nothing running, or we've explicitly hidden
  // it on this page until the next push.
  if (!activeFlashSession || !timerState || hiddenUntilNextPush) {
    return null;
  }

  const phaseLabel = timerState.phase === "delay" ? "Delay" : "Quiz Timer";
  const phaseColor = timerState.phase === "delay" ? "#4caf50" : "#f44336";

  return (
    <Box
      sx={{
        position: "fixed",
        right: 24,
        bottom: 24,
        zIndex: 1300,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 1.5,
          pr: 1,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          bgcolor: "#121212",
          borderRadius: 2,
          border: "1px solid #333",
          minWidth: 220,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(156, 39, 176, 0.15)",
            color: "#E1BEE7",
          }}
        >
          <FlashIcon fontSize="small" />
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0, overflow: "hidden" }}>
          <Typography
            variant="caption"
            sx={{ color: "#aaa", textTransform: "uppercase", letterSpacing: 0.8 }}
          >
            Flash Quiz
          </Typography>

          {currentSong && (
            <>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: 600, color: "#fff" }}
              >
                {currentSong.name}
              </Typography>
              {currentSong.artist && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: "#bbb" }}
                >
                  {currentSong.artist}
                </Typography>
              )}
            </>
          )}

          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "#fff",
              lineHeight: 1.2,
            }}
          >
            {formatTimer(timerState.remainingSeconds)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: phaseColor, fontWeight: 500 }}
          >
            {phaseLabel}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", ml: 0.5 }}>
          {loadingSession && (
            <CircularProgress size={18} sx={{ color: "#888", mr: 1 }} />
          )}
          <Tooltip title="Stop current flash quiz question">
            <span>
              <IconButton
                size="small"
                onClick={handleStopQuestion}
                disabled={stoppingQuestion}
                sx={{
                  color: phaseColor,
                  "&:hover": {
                    bgcolor:
                      phaseColor === "#4caf50"
                        ? "rgba(76, 175, 80, 0.12)"
                        : "rgba(244, 67, 54, 0.12)",
                  },
                }}
              >
                {stoppingQuestion ? (
                  <CircularProgress size={18} sx={{ color: phaseColor }} />
                ) : (
                  <StopIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
};

export default FlashQuizStatusBox;


